import React, { useState, useEffect, useRef } from 'react'
import { Wifi, WifiOff, Mail, MessageCircle, Smartphone, Building2, Eye, Filter, Zap, Clock, AlertCircle, CheckCircle2, Plane, Calendar } from 'lucide-react'
import type { Message, DataSourceStatus, Event } from '../../types/mockup'
import { MessageItem } from './MessageItem'
import { generateIncomingMessage } from '../../utils/scheduleSimulator'

interface DataSourcePanelProps {
  messages: Message[]
  dataSourceStatus: DataSourceStatus
  onMessageClick?: (message: Message) => void
  roleView?: 'coordinator' | 'duty_manager'
  simulateMessages?: boolean
  event?: Event
}

export const DataSourcePanel: React.FC<DataSourcePanelProps> = ({
  messages,
  dataSourceStatus,
  onMessageClick,
  roleView = 'coordinator',
  simulateMessages = false,
  event
}) => {
  const [localMessages, setLocalMessages] = useState<Message[]>(messages)
  const [selectedMessage, setSelectedMessage] = useState<Message | null>(null)
  const [showModal, setShowModal] = useState(false)
  const [showOperaImport, setShowOperaImport] = useState(false)
  const [activeFilter, setActiveFilter] = useState<'all' | 'unprocessed' | 'urgent'>('all')
  const [activeSourceFilter, setActiveSourceFilter] = useState<'all' | 'email' | 'sms' | 'whatsapp' | 'opera' | 'amadeus' | 'tripleseat'>('all')
  const intervalRef = useRef<NodeJS.Timeout>()
  
  useEffect(() => {
    setLocalMessages(messages)
  }, [messages])
  
  useEffect(() => {
    if (simulateMessages && event) {
      intervalRef.current = setInterval(() => {
        const newMessage = generateIncomingMessage(event, new Date())
        setLocalMessages(prev => [newMessage, ...prev])
      }, 8000 + Math.random() * 7000)
      
      return () => {
        if (intervalRef.current) clearInterval(intervalRef.current)
      }
    }
  }, [simulateMessages, event])
  
  const handleMessageClick = (message: Message) => {
    setSelectedMessage(message)
    setShowModal(true)
    onMessageClick?.(message)
  }
  
  const filterMessagesByRole = (msgs: Message[]) => {
    if (roleView === 'duty_manager') {
      // Venue manager sees operational and urgent messages
      return msgs.filter(m => 
        m.source === 'opera' || 
        m.content.toLowerCase().includes('urgent') ||
        m.content.toLowerCase().includes('emergency') ||
        m.content.toLowerCase().includes('guest') ||
        m.content.toLowerCase().includes('room')
      )
    }
    // Wedding coordinator sees all event-related messages
    return msgs
  }

  const filterMessages = (msgs: Message[]) => {
    let filtered = filterMessagesByRole(msgs)
    
    // Apply content filter
    switch (activeFilter) {
      case 'unprocessed':
        filtered = filtered.filter(m => !m.isProcessed)
        break
      case 'urgent':
        filtered = filtered.filter(m => 
          m.content.toLowerCase().includes('urgent') ||
          m.content.toLowerCase().includes('emergency') ||
          m.content.toLowerCase().includes('asap')
        )
        break
    }

    // Apply source filter
    if (activeSourceFilter !== 'all') {
      filtered = filtered.filter(m => m.source === activeSourceFilter)
    }

    return filtered
  }

  const filteredMessages = filterMessages(localMessages)
  const sortedMessages = [...filteredMessages].sort((a, b) => 
    b.timestamp.getTime() - a.timestamp.getTime()
  )

  const getSourceIcon = (source: string) => {
    switch (source) {
      case 'email': return <Mail size={16} />
      case 'sms': return <MessageCircle size={16} />
      case 'whatsapp': return <Smartphone size={16} />
      case 'opera': return <Building2 size={16} />
      case 'amadeus': return <Plane size={16} />
      case 'tripleseat': return <Calendar size={16} />
      default: return <MessageCircle size={16} />
    }
  }

  const getSourceStats = () => {
    const stats = {
      total: localMessages.length,
      unprocessed: localMessages.filter(m => !m.isProcessed).length,
      urgent: localMessages.filter(m => 
        m.content.toLowerCase().includes('urgent') ||
        m.content.toLowerCase().includes('emergency') ||
        m.content.toLowerCase().includes('asap')
      ).length,
      // Source-specific message counts
      email: localMessages.filter(m => m.source === 'email').length,
      sms: localMessages.filter(m => m.source === 'sms').length,
      whatsapp: localMessages.filter(m => m.source === 'whatsapp').length,
      opera: localMessages.filter(m => m.source === 'opera').length,
      amadeus: localMessages.filter(m => m.source === 'amadeus').length,
      tripleseat: localMessages.filter(m => m.source === 'tripleseat').length
    }
    return stats
  }

  const stats = getSourceStats()

  return (
    <>
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        background: 'linear-gradient(135deg, #f8f9ff 0%, #f1f3ff 100%)',
        borderRadius: '16px',
        border: '1px solid #e2e8f0',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 24px 16px 24px',
          background: 'linear-gradient(135deg, #6a1b9a 0%, #8e24aa 100%)',
          color: 'white',
          borderBottom: '1px solid rgba(255,255,255,0.1)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '12px',
              background: 'rgba(255,255,255,0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '20px'
            }}>
              <Zap size={20} />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: '20px', fontWeight: 600 }}>Live Data Sources</h3>
              <div style={{ fontSize: '14px', opacity: 0.9 }}>Real-time event communications</div>
            </div>
          </div>
          
          {/* Controls */}
          <div style={{ display: 'flex', gap: '8px', marginTop: '12px', alignItems: 'center' }}>
            <select 
              value={roleView}
              onChange={() => {}}
              style={{
                padding: '6px 12px',
                background: 'rgba(255,255,255,0.15)',
                border: 'none',
                borderRadius: '8px',
                color: 'white',
                fontSize: '12px',
                outline: 'none'
              }}
            >
              <option value="coordinator" style={{ color: '#374151' }}>Wedding Coordinator</option>
              <option value="duty_manager" style={{ color: '#374151' }}>Venue Manager</option>
            </select>
            
            <button 
              onClick={() => setActiveSourceFilter('all')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 12px',
                background: activeSourceFilter === 'all' ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.15)',
                border: 'none',
                borderRadius: '8px',
                color: 'white',
                fontSize: '12px',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.25)'}
              onMouseLeave={(e) => e.currentTarget.style.background = activeSourceFilter === 'all' ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.15)'}
            >
              <Eye size={14} />
              Show All Sources
            </button>
            
            {dataSourceStatus.opera.connected && (
              <button 
                onClick={() => setShowOperaImport(!showOperaImport)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '6px 12px',
                  background: showOperaImport ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.15)',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  fontSize: '12px',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.25)'}
                onMouseLeave={(e) => e.currentTarget.style.background = showOperaImport ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.15)'}
              >
                <Building2 size={14} />
                OPERA Details
              </button>
            )}
          </div>
        </div>

        {/* Data Source Indicators */}
        <div style={{
          padding: '20px 24px',
          background: 'white',
          borderBottom: '1px solid #e5e7eb'
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '12px' }}>
            {/* Email */}
            <div 
              onClick={() => setActiveSourceFilter(activeSourceFilter === 'email' ? 'all' : 'email')}
              style={{
                padding: '16px',
                background: activeSourceFilter === 'email' ? '#dbeafe' : (dataSourceStatus.email.connected ? '#f0fdf4' : '#fef2f2'),
                border: `1px solid ${activeSourceFilter === 'email' ? '#3b82f6' : (dataSourceStatus.email.connected ? '#bbf7d0' : '#fecaca')}`,
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                if (activeSourceFilter !== 'email') {
                  e.currentTarget.style.background = '#f8fafc'
                }
              }}
              onMouseLeave={(e) => {
                if (activeSourceFilter !== 'email') {
                  e.currentTarget.style.background = dataSourceStatus.email.connected ? '#f0fdf4' : '#fef2f2'
                }
              }}
            >
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '10px',
                background: dataSourceStatus.email.connected ? '#22c55e' : '#ef4444',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white'
              }}>
                <Mail size={20} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '14px', fontWeight: 600, color: '#374151' }}>Email</div>
                <div style={{ fontSize: '12px', color: '#6b7280' }}>
                  {stats.email} messages
                </div>
              </div>
              <div style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: dataSourceStatus.email.connected ? '#22c55e' : '#ef4444'
              }} />
            </div>

            {/* SMS */}
            <div 
              onClick={() => setActiveSourceFilter(activeSourceFilter === 'sms' ? 'all' : 'sms')}
              style={{
                padding: '16px',
                background: activeSourceFilter === 'sms' ? '#dbeafe' : (dataSourceStatus.sms.connected ? '#f0fdf4' : '#fef2f2'),
                border: `1px solid ${activeSourceFilter === 'sms' ? '#3b82f6' : (dataSourceStatus.sms.connected ? '#bbf7d0' : '#fecaca')}`,
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                if (activeSourceFilter !== 'sms') {
                  e.currentTarget.style.background = '#f8fafc'
                }
              }}
              onMouseLeave={(e) => {
                if (activeSourceFilter !== 'sms') {
                  e.currentTarget.style.background = dataSourceStatus.sms.connected ? '#f0fdf4' : '#fef2f2'
                }
              }}
            >
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '10px',
                background: dataSourceStatus.sms.connected ? '#22c55e' : '#ef4444',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white'
              }}>
                <MessageCircle size={20} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '14px', fontWeight: 600, color: '#374151' }}>SMS</div>
                <div style={{ fontSize: '12px', color: '#6b7280' }}>
                  {stats.sms} messages
                </div>
              </div>
              <div style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: dataSourceStatus.sms.connected ? '#22c55e' : '#ef4444'
              }} />
            </div>

            {/* WhatsApp */}
            <div 
              onClick={() => setActiveSourceFilter(activeSourceFilter === 'whatsapp' ? 'all' : 'whatsapp')}
              style={{
                padding: '16px',
                background: activeSourceFilter === 'whatsapp' ? '#dbeafe' : (dataSourceStatus.whatsapp.connected ? '#f0fdf4' : '#fef2f2'),
                border: `1px solid ${activeSourceFilter === 'whatsapp' ? '#3b82f6' : (dataSourceStatus.whatsapp.connected ? '#bbf7d0' : '#fecaca')}`,
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                if (activeSourceFilter !== 'whatsapp') {
                  e.currentTarget.style.background = '#f8fafc'
                }
              }}
              onMouseLeave={(e) => {
                if (activeSourceFilter !== 'whatsapp') {
                  e.currentTarget.style.background = dataSourceStatus.whatsapp.connected ? '#f0fdf4' : '#fef2f2'
                }
              }}
            >
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '10px',
                background: dataSourceStatus.whatsapp.connected ? '#22c55e' : '#ef4444',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white'
              }}>
                <Smartphone size={20} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '14px', fontWeight: 600, color: '#374151' }}>WhatsApp</div>
                <div style={{ fontSize: '12px', color: '#6b7280' }}>
                  {stats.whatsapp} messages
                </div>
              </div>
              <div style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: dataSourceStatus.whatsapp.connected ? '#22c55e' : '#ef4444'
              }} />
            </div>

            {/* OPERA */}
            {dataSourceStatus.opera && (
              <div 
                onClick={() => setActiveSourceFilter(activeSourceFilter === 'opera' ? 'all' : 'opera')}
                style={{
                  padding: '16px',
                  background: activeSourceFilter === 'opera' ? '#dbeafe' : (dataSourceStatus.opera.connected ? '#f0fdf4' : '#fef2f2'),
                  border: `1px solid ${activeSourceFilter === 'opera' ? '#3b82f6' : (dataSourceStatus.opera.connected ? '#bbf7d0' : '#fecaca')}`,
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  if (activeSourceFilter !== 'opera') {
                    e.currentTarget.style.background = '#f8fafc'
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeSourceFilter !== 'opera') {
                    e.currentTarget.style.background = dataSourceStatus.opera.connected ? '#f0fdf4' : '#fef2f2'
                  }
                }}
              >
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '10px',
                  background: dataSourceStatus.opera.connected ? '#22c55e' : '#ef4444',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white'
                }}>
                  <Building2 size={20} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '14px', fontWeight: 600, color: '#374151' }}>OPERA</div>
                  <div style={{ fontSize: '12px', color: '#6b7280' }}>
                    {stats.opera} messages
                  </div>
                </div>
                <div style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: dataSourceStatus.opera.connected ? '#22c55e' : '#ef4444'
                }} />
              </div>
            )}

            {/* Amadeus Delphi */}
            {dataSourceStatus.amadeus && (
              <div 
                onClick={() => setActiveSourceFilter(activeSourceFilter === 'amadeus' ? 'all' : 'amadeus')}
                style={{
                  padding: '16px',
                  background: activeSourceFilter === 'amadeus' ? '#dbeafe' : (dataSourceStatus.amadeus.connected ? '#f0fdf4' : '#fef2f2'),
                  border: `1px solid ${activeSourceFilter === 'amadeus' ? '#3b82f6' : (dataSourceStatus.amadeus.connected ? '#bbf7d0' : '#fecaca')}`,
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  if (activeSourceFilter !== 'amadeus') {
                    e.currentTarget.style.background = '#f8fafc'
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeSourceFilter !== 'amadeus') {
                    e.currentTarget.style.background = dataSourceStatus.amadeus.connected ? '#f0fdf4' : '#fef2f2'
                  }
                }}
              >
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '10px',
                  background: dataSourceStatus.amadeus.connected ? '#22c55e' : '#ef4444',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white'
                }}>
                  <Plane size={20} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '14px', fontWeight: 600, color: '#374151' }}>Amadeus Delphi</div>
                  <div style={{ fontSize: '12px', color: '#6b7280' }}>
                    {stats.amadeus} messages
                  </div>
                </div>
                <div style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: dataSourceStatus.amadeus.connected ? '#22c55e' : '#ef4444'
                }} />
              </div>
            )}

            {/* TripleSeat */}
            {dataSourceStatus.tripleseat && (
              <div 
                onClick={() => setActiveSourceFilter(activeSourceFilter === 'tripleseat' ? 'all' : 'tripleseat')}
                style={{
                  padding: '16px',
                  background: activeSourceFilter === 'tripleseat' ? '#dbeafe' : (dataSourceStatus.tripleseat.connected ? '#f0fdf4' : '#fef2f2'),
                  border: `1px solid ${activeSourceFilter === 'tripleseat' ? '#3b82f6' : (dataSourceStatus.tripleseat.connected ? '#bbf7d0' : '#fecaca')}`,
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  if (activeSourceFilter !== 'tripleseat') {
                    e.currentTarget.style.background = '#f8fafc'
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeSourceFilter !== 'tripleseat') {
                    e.currentTarget.style.background = dataSourceStatus.tripleseat.connected ? '#f0fdf4' : '#fef2f2'
                  }
                }}
              >
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '10px',
                  background: dataSourceStatus.tripleseat.connected ? '#22c55e' : '#ef4444',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white'
                }}>
                  <Calendar size={20} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '14px', fontWeight: 600, color: '#374151' }}>TripleSeat</div>
                  <div style={{ fontSize: '12px', color: '#6b7280' }}>
                    {stats.tripleseat} messages
                  </div>
                </div>
                <div style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: dataSourceStatus.tripleseat.connected ? '#22c55e' : '#ef4444'
                }} />
              </div>
            )}
          </div>
        </div>
        
        {/* OPERA Import Panel */}
        {showOperaImport && dataSourceStatus.opera.connected && (
          <div style={{
            padding: '20px 24px',
            background: '#f8f9fa',
            borderBottom: '1px solid #e5e7eb'
          }}>
            <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600, color: '#374151' }}>
              Sarah & James Wedding - OPERA Event Details
            </h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              <div>
                <h5 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 600, color: '#6b7280' }}>
                  Guest Accommodations
                </h5>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{
                    padding: '8px 12px',
                    background: 'white',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb',
                    fontSize: '13px',
                    color: '#374151'
                  }}>
                    Wedding Block - 18 rooms booked
                  </div>
                  <div style={{
                    padding: '8px 12px',
                    background: 'white',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb',
                    fontSize: '13px',
                    color: '#374151'
                  }}>
                    Bridal Suite - Confirmed
                  </div>
                  <div style={{
                    padding: '8px 12px',
                    background: 'white',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb',
                    fontSize: '13px',
                    color: '#374151'
                  }}>
                    Check-ins: 16/18 arrived
                  </div>
                </div>
              </div>
              <div>
                <h5 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 600, color: '#6b7280' }}>
                  Event Spaces (BEO)
                </h5>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{
                    padding: '8px 12px',
                    background: 'white',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb',
                    fontSize: '13px',
                    color: '#374151'
                  }}>
                    Ceremony - Grand Ballroom (3:30 PM)
                  </div>
                  <div style={{
                    padding: '8px 12px',
                    background: 'white',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb',
                    fontSize: '13px',
                    color: '#374151'
                  }}>
                    Reception - Garden Terrace (6:00 PM)
                  </div>
                  <div style={{
                    padding: '8px 12px',
                    background: 'white',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb',
                    fontSize: '13px',
                    color: '#374151'
                  }}>
                    Cocktails - Rooftop (4:30 PM)
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Message Filters */}
        <div style={{
          padding: '16px 24px',
          background: 'white',
          borderBottom: '1px solid #e5e7eb'
        }}>
          {/* Content Filters */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <div style={{ display: 'flex', gap: '8px' }}>
              {[
                { id: 'all', label: `All (${stats.total})`, icon: Eye },
                { id: 'unprocessed', label: `Unprocessed (${stats.unprocessed})`, icon: AlertCircle },
                { id: 'urgent', label: `Urgent (${stats.urgent})`, icon: Clock }
              ].map(filter => (
                <button
                  key={filter.id}
                  onClick={() => setActiveFilter(filter.id as any)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '6px 12px',
                    background: activeFilter === filter.id ? '#3b82f6' : '#f3f4f6',
                    color: activeFilter === filter.id ? 'white' : '#6b7280',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '12px',
                    fontWeight: 500,
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  <filter.icon size={14} />
                  {filter.label}
                </button>
              ))}
            </div>
            
            <div style={{ fontSize: '12px', color: '#6b7280' }}>
              {filteredMessages.length} of {localMessages.length} messages
            </div>
          </div>

        </div>
        
        {/* Messages Feed */}
        <div style={{
          flex: 1,
          overflow: 'auto',
          padding: '16px 24px 20px 24px'
        }}>
          {sortedMessages.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {sortedMessages.map((message, index) => (
                <div
                  key={message.id}
                  onClick={() => handleMessageClick(message)}
                  style={{
                    padding: '16px',
                    background: message.isProcessed ? '#f8f9fa' : 'white',
                    border: `1px solid ${message.isProcessed ? '#e5e7eb' : '#3b82f6'}`,
                    borderRadius: '12px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    boxShadow: message.isProcessed ? 'none' : '0 2px 4px rgba(59,130,246,0.1)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-1px)'
                    e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)'
                    e.currentTarget.style.boxShadow = message.isProcessed ? 'none' : '0 2px 4px rgba(59,130,246,0.1)'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                    {/* Source Icon */}
                    <div style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '8px',
                      background: message.isProcessed ? '#e5e7eb' : '#3b82f6',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      flexShrink: 0
                    }}>
                      {getSourceIcon(message.source)}
                    </div>

                    {/* Message Content */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: '4px'
                      }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          <span style={{ fontSize: '14px', fontWeight: 600, color: '#374151' }}>
                            {message.from}
                          </span>
                          <span style={{
                            fontSize: '11px',
                            padding: '2px 6px',
                            background: '#f3f4f6',
                            color: '#6b7280',
                            borderRadius: '4px',
                            textTransform: 'uppercase',
                            fontWeight: 500
                          }}>
                            {message.source}
                          </span>
                        </div>
                        
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          <span style={{ fontSize: '12px', color: '#9ca3af' }}>
                            {message.timestamp.toLocaleTimeString()}
                          </span>
                          {message.isProcessed ? (
                            <CheckCircle2 size={16} style={{ color: '#22c55e' }} />
                          ) : (
                            <div style={{
                              width: '8px',
                              height: '8px',
                              borderRadius: '50%',
                              background: '#3b82f6'
                            }} />
                          )}
                        </div>
                      </div>
                      
                      <div style={{
                        fontSize: '14px',
                        color: '#374151',
                        lineHeight: '1.4',
                        marginBottom: '8px'
                      }}>
                        {message.content}
                      </div>
                      
                      {message.parsedIntent && (
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          fontSize: '12px',
                          color: '#6b7280'
                        }}>
                          <span>Intent: {message.parsedIntent.action}</span>
                          <span>•</span>
                          <span>Confidence: {(message.parsedIntent.confidence * 100).toFixed(0)}%</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{
              textAlign: 'center',
              padding: '40px',
              color: '#6b7280'
            }}>
              <MessageCircle size={48} style={{ margin: '0 auto 16px', opacity: 0.3 }} />
              <h3 style={{ fontSize: '16px', fontWeight: 500, marginBottom: '8px' }}>
                No messages found
              </h3>
              <p style={{ fontSize: '14px' }}>
                {activeFilter === 'all' 
                  ? 'No messages have been received yet'
                  : `No ${activeFilter} messages found`
                }
              </p>
            </div>
          )}
        </div>
      </div>
      
      {/* Modal */}
      {showModal && selectedMessage && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            width: '100%',
            maxWidth: '600px',
            maxHeight: '80vh',
            overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            {/* Modal Header */}
            <div style={{
              padding: '20px 24px',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#374151' }}>
                Message Details
              </h3>
              <button 
                onClick={() => setShowModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '18px',
                  color: '#6b7280',
                  cursor: 'pointer',
                  padding: '4px',
                  borderRadius: '4px'
                }}
              >
                ✕
              </button>
            </div>
            
            {/* Modal Body */}
            <div style={{ padding: '24px', overflow: 'auto' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div>
                  <label style={{ fontSize: '12px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>
                    From
                  </label>
                  <div style={{ fontSize: '14px', color: '#374151', marginTop: '4px' }}>
                    {selectedMessage.from}
                  </div>
                </div>
                
                <div>
                  <label style={{ fontSize: '12px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>
                    Source
                  </label>
                  <div style={{ fontSize: '14px', color: '#374151', marginTop: '4px' }}>
                    {selectedMessage.source.toUpperCase()}
                  </div>
                </div>
                
                <div>
                  <label style={{ fontSize: '12px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>
                    Time
                  </label>
                  <div style={{ fontSize: '14px', color: '#374151', marginTop: '4px' }}>
                    {selectedMessage.timestamp.toLocaleString()}
                  </div>
                </div>
                
                <div>
                  <label style={{ fontSize: '12px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>
                    Content
                  </label>
                  <div style={{
                    fontSize: '14px',
                    color: '#374151',
                    marginTop: '4px',
                    padding: '12px',
                    background: '#f8f9fa',
                    borderRadius: '8px',
                    lineHeight: '1.5'
                  }}>
                    {selectedMessage.content}
                  </div>
                </div>
                
                {selectedMessage.parsedIntent && (
                  <div>
                    <label style={{ fontSize: '12px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>
                      Parsed Intent
                    </label>
                    <div style={{
                      marginTop: '8px',
                      padding: '12px',
                      background: '#f0fdf4',
                      border: '1px solid #bbf7d0',
                      borderRadius: '8px'
                    }}>
                      <div style={{ fontSize: '14px', color: '#374151', marginBottom: '4px' }}>
                        <strong>Action:</strong> {selectedMessage.parsedIntent.action}
                      </div>
                      <div style={{ fontSize: '14px', color: '#374151' }}>
                        <strong>Confidence:</strong> {(selectedMessage.parsedIntent.confidence * 100).toFixed(0)}%
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Modal Actions */}
            <div style={{
              padding: '20px 24px',
              borderTop: '1px solid #e5e7eb',
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => setShowModal(false)}
                style={{
                  padding: '8px 16px',
                  background: '#f3f4f6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: 'pointer'
                }}
              >
                Dismiss
              </button>
              <button
                style={{
                  padding: '8px 16px',
                  background: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: 'pointer'
                }}
              >
                Process Message
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default DataSourcePanel